import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { CALENDLY_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Server-side price catalog is the source of truth. This structure defines display order + copy per lookup_key.
const PLAN_ORDER = [
  {
    lookup_key: "educator_monthly",
    yearly_lookup_key: "educator_yearly",
    name: "Educator",
    color: "#FFFFFF",
    text: "#1A1A1A",
    features: [
      "One educator seat",
      "TLS app + projects + pathways",
      "Full lesson resource library",
      "Templates and ongoing updates",
    ],
    tagline: "For the solo educator ready to shift.",
  },
  {
    lookup_key: "school_starter_yearly",
    name: "School Starter",
    color: "#FFD700",
    text: "#1A1A1A",
    features: [
      "Up to 5 educators",
      "School licence",
      "Full resource library",
      "App access + onboarding",
    ],
    tagline: "A pilot team of five, one school licence.",
  },
  {
    lookup_key: "school_plus_yearly",
    name: "School Plus",
    color: "#FF6600",
    text: "#FFFFFF",
    features: [
      "Up to 15 educators",
      "Everything in Starter",
      "Implementation resources",
      "1 × annual online strategy session",
    ],
    tagline: "For faculties going wider, with strategy support.",
  },
  {
    lookup_key: "whole_school_yearly",
    name: "Whole School",
    color: "#FF007F",
    text: "#FFFFFF",
    highlight: true,
    features: [
      "Up to 30 educators",
      "Full resources, app, onboarding",
      "Leadership access + support",
      "Priority helpdesk",
    ],
    tagline: "Adopt across the whole staff, end-to-end.",
  },
  {
    lookup_key: "network_district_yearly",
    name: "Network / District",
    color: "#1A1A1A",
    text: "#FFFFFF",
    features: [
      "Up to 10 schools · 5 educators each",
      "Central admin + rollout support",
      "Shared reporting across sites",
      "Custom onboarding roadmap",
    ],
    tagline: "For councils, networks and multi-school districts.",
  },
];

const ONE_TIME_ORDER = [
  {
    lookup_key: "consulting_60min",
    name: "Specialist Consulting",
    unit: "60 minutes",
    color: "#0055FF",
    text: "#FFFFFF",
    features: [
      "1:1 with Julija McDowell",
      "STEAM · maker · facilitator strategy",
      "Follow-up recap + action list",
    ],
  },
  {
    lookup_key: "steam_pd_day",
    name: "STEAM PD Day",
    unit: "per teacher / day",
    color: "#00C2B8",
    text: "#1A1A1A",
    features: [
      "Full-day hands-on PD",
      "Practical, classroom-ready",
      "Team booking discounts available",
    ],
  },
  {
    lookup_key: "discovery_deposit",
    name: "Discovery Deposit",
    unit: "one-time",
    color: "#FFFFFF",
    text: "#1A1A1A",
    features: [
      "Reserves a full discovery engagement",
      "Credited back on signed proposal",
      "Refundable within 14 days",
    ],
  },
];

const money = (amount, currency) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: (currency || "aud").toUpperCase(),
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

export default function PricingPage() {
  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState("year"); // year | month
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/pricing/catalog`)
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Couldn’t load pricing. Please refresh."));
  }, []);

  const priceMap = useMemo(() => {
    if (!catalog) return {};
    return Object.fromEntries(catalog.map((p) => [p.lookup_key, p]));
  }, [catalog]);

  const checkout = async (lookupKey, quantity = 1) => {
    if (!lookupKey) return;
    setLoadingKey(lookupKey);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn’t start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
    <div data-testid="pricing-page">
      <PageHero
        eyebrow="Pricing · Plans & Services"
        accent="#FF007F"
        title={
          <>
            One{" "}
            <span className="bg-[#FFD700] px-2 inline-block">framework</span>.
            Every school size.
          </>
        }
        intro="Educator plans start at $29.99 per month. School licences from $1,990 per year. All prices in AUD. Cancel or scale up any time."
        ctaLabel="Talk to us first"
        ctaTo={CALENDLY_URL}
        ctaExternal
      />

      {/* Billing toggle */}
      <section className="bg-white border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-8 flex items-center justify-between gap-4 flex-wrap">
          <p className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">
            The TinkerShift Platform · subscription plans
          </p>
          <div
            role="tablist"
            aria-label="Billing period"
            className="inline-flex items-center gap-1 bg-white border-2 border-[#1A1A1A] rounded-full p-1 nb-shadow-sm"
          >
            {["year", "month"].map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={billing === k}
                onClick={() => setBilling(k)}
                data-testid={`billing-toggle-${k}`}
                className={`px-4 py-1.5 text-xs uppercase font-bold tracking-widest rounded-full transition-colors ${
                  billing === k
                    ? "bg-[#1A1A1A] text-white"
                    : "text-[#1A1A1A]"
                }`}
              >
                {k === "year" ? "Yearly · save" : "Monthly"}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Plans grid */}
      <section
        data-testid="pricing-plans-section"
        className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A] relative"
      >
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
          {!catalog ? (
            <div className="min-h-[360px] flex items-center justify-center text-[#4A4A4A]">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
              {PLAN_ORDER.map((plan, idx) => {
                const monthlyKey = plan.lookup_key;
                const yearlyKey = plan.yearly_lookup_key;
                const hasBoth = !!yearlyKey && priceMap[yearlyKey];
                const activeKey = hasBoth
                  ? billing === "year"
                    ? yearlyKey
                    : monthlyKey
                  : monthlyKey;
                const priceData = priceMap[activeKey];
                if (!priceData) return null;

                return (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: 0.7,
                      delay: idx * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    style={{
                      backgroundColor: plan.color,
                      color: plan.text,
                    }}
                    data-testid={`plan-card-${plan.name.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className={`relative rounded-3xl border-2 border-[#1A1A1A] p-6 md:p-7 nb-shadow nb-lift flex flex-col ${
                      plan.highlight ? "lg:-translate-y-4" : ""
                    }`}
                  >
                    {plan.highlight && (
                      <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold nb-shadow-sm">
                        <Sparkles size={12} strokeWidth={2.5} /> Most Popular
                      </span>
                    )}
                    <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                      Plan 0{idx + 1}
                    </p>
                    <h3 className="mt-1 font-display font-black text-2xl md:text-3xl leading-tight tracking-tight">
                      {plan.name}
                    </h3>
                    <p className="mt-2 text-sm font-semibold opacity-90 min-h-[3rem]">
                      {plan.tagline}
                    </p>
                    <div className="mt-6">
                      <p className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">
                        {priceData.lookup_key === "network_district_yearly"
                          ? "From "
                          : ""}
                        {money(priceData.amount, priceData.currency)}
                      </p>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-widest opacity-80">
                        {priceData.interval
                          ? `per ${priceData.interval} · AUD`
                          : "one-time · AUD"}
                      </p>
                    </div>
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm font-semibold">
                          <span
                            className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-md border"
                            style={{
                              backgroundColor:
                                plan.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                              color: plan.text === "#FFFFFF" ? "#1A1A1A" : "#FFFFFF",
                              borderColor: plan.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                            }}
                          >
                            <Check size={11} strokeWidth={3} />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => checkout(activeKey)}
                      disabled={loadingKey === activeKey}
                      data-testid={`plan-cta-${plan.name.toLowerCase().replace(/[\s/]+/g, "-")}`}
                      className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-bold uppercase text-xs tracking-widest border-2 nb-shadow-sm nb-press ${
                        plan.text === "#FFFFFF"
                          ? "bg-white text-[#1A1A1A] border-[#1A1A1A]"
                          : "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      } disabled:opacity-70`}
                    >
                      {loadingKey === activeKey ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <ArrowUpRight size={14} strokeWidth={2.5} />
                      )}
                      Start plan
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Consulting / one-time services */}
      <section
        data-testid="one-time-services-section"
        className="bg-white border-b-2 border-[#1A1A1A]"
      >
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-24">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF6600]">
              Consulting &amp; PD · one-time
            </p>
            <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter max-w-4xl">
              Real hours with real experts.{" "}
              <span className="italic text-[#4A4A4A]">
                Priced clearly.
              </span>
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {ONE_TIME_ORDER.map((s, idx) => {
              const p = priceMap[s.lookup_key];
              return (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.7,
                    delay: idx * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  data-testid={`one-time-card-${s.lookup_key}`}
                  style={{ backgroundColor: s.color, color: s.text }}
                  className="rounded-3xl border-2 border-[#1A1A1A] p-7 nb-shadow nb-lift flex flex-col"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                    Service
                  </p>
                  <h3 className="mt-1 font-display font-black text-2xl md:text-3xl leading-tight tracking-tight">
                    {s.name}
                  </h3>
                  <p className="mt-4 font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">
                    {p ? money(p.amount, p.currency) : "—"}
                  </p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-widest opacity-80">
                    {s.unit} · AUD
                  </p>
                  <ul className="mt-6 space-y-2 flex-1">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm font-semibold">
                        <span
                          className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-md border"
                          style={{
                            backgroundColor:
                              s.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                            color: s.text === "#FFFFFF" ? "#1A1A1A" : "#FFFFFF",
                            borderColor: s.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                          }}
                        >
                          <Check size={11} strokeWidth={3} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => checkout(s.lookup_key)}
                    disabled={loadingKey === s.lookup_key}
                    data-testid={`one-time-cta-${s.lookup_key}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-bold uppercase text-xs tracking-widest border-2 nb-shadow-sm nb-press ${
                      s.text === "#FFFFFF"
                        ? "bg-white text-[#1A1A1A] border-[#1A1A1A]"
                        : "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    } disabled:opacity-70`}
                  >
                    {loadingKey === s.lookup_key ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowUpRight size={14} strokeWidth={2.5} />
                    )}
                    Book &amp; pay
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ / trust strip */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              t: "Prices in AUD",
              b: "All checkout is in Australian dollars. Payments processed securely via Stripe.",
            },
            {
              t: "Cancel any time",
              b: "Subscriptions can be cancelled or upgraded at renewal. No hidden lock-in.",
            },
            {
              t: "Need a custom quote?",
              b: "Networks, districts and multi-school rollouts — book a call and we’ll scope it.",
            },
          ].map((x) => (
            <div
              key={x.t}
              className="rounded-2xl bg-white border-2 border-[#1A1A1A] p-5 nb-shadow-sm"
            >
              <p className="font-display font-black text-lg">{x.t}</p>
              <p className="mt-2 text-sm text-[#4A4A4A] font-medium">{x.b}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
